import { useEffect, useState, useId } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { ErrorBox, Page, useApi } from '../ui/kit';
import { TodaySection, type TodayLine } from './launchpad';
import { AdsCard } from '../ui/ads-parts';

// what each setup step means, where it is done, and its icon
const STEPS: Record<string, { to: string; hint: string; cta: string; icon: string }> = {
  context: { to: '/context', hint: 'What you sell and to whom, with sources', cta: 'Add business context', icon: 'M6 3h9l4 4v14H6zM14 3v5h5M9 13h7M9 17h7' },
  markets: { to: '/icp', hint: 'The countries you sell into, proposed from your site; you confirm', cta: 'Set your markets', icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18' },
  import: { to: '/import', hint: 'Your accounts and won/lost deals', cta: 'Import accounts', icon: 'M12 3v12M7 10l5 5 5-5M4 21h16' },
  crm: { to: '/integrations', hint: 'HubSpot or Salesforce, read-only first', cta: 'Connect your CRM', icon: 'M8 7a4 4 0 1 0 0 10M16 7a4 4 0 1 1 0 10M8 12h8' },
  icp: { to: '/icp', hint: 'A person approves who you sell to', cta: 'Review the ICP', icon: 'M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6zM9 12l2 2 4-4' },
  baseline: { to: '/reports?tab=baseline', hint: 'Four numbers from before Outlevo: acceptance rate, meetings, research time, time lost', cta: 'Record a baseline', icon: 'M4 20V10M10 20V4M16 20v-7M22 20H2' },
  qualified: { to: '/accounts', hint: 'Research a site and qualify it against the ICP', cta: 'Research an account', icon: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3' },
};
const Icon = ({ d }: { d: string }) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;

type Onboarding = { steps: Array<{ key: string; label: string; done: boolean }>; done: number; total: number };

/** Get started (as Explee): a small widget in the corner with the setup steps, instead of a page section. */
function GetStarted() {
  const s = useApi<Onboarding>('/portal/onboarding/state');
  const [open, setOpen] = useState(false);
  if (!s.data || s.data.done >= s.data.total) return null;
  const next = s.data.steps.find((st) => !st.done);
  return (
    <div className={`get-started${open ? ' open' : ''}`}>
      {open ? (
        <div className="gs-pop" role="dialog" aria-label="Get started">
          <ol>{s.data.steps.map((st) => (
            <li key={st.key} className={st.done ? 'done' : st.key === next?.key ? 'next' : ''}>
              <span className="own-n" aria-hidden="true">{st.done ? '✓' : ''}</span>
              {st.done ? <span>{st.label}</span> : <Link to={STEPS[st.key]?.to ?? '/'} onClick={() => setOpen(false)}>{st.label}</Link>}
            </li>
          ))}</ol>
        </div>
      ) : null}
      <button type="button" className="gs-trigger" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span>Get started</span>
        <span className="gs-bar" aria-hidden="true"><i style={{ width: `${(s.data.done / s.data.total) * 100}%` }} /></span>
        <span>{s.data.done} of {s.data.total}</span>
      </button>
    </div>
  );
}

type Dash = {
  banner: { tone: string; title: string; text: string; cta: { label: string; to: string } } | null;
  money: { available: number; daily_budget: number | null; spent_today: number };
  referral: { credits_each: number; usd_each: number };
  performance: { days: number; series: Array<{ day: string; sent: number; replies: number; researched: number; credits: number }>; totals: Record<'sent' | 'replies' | 'researched' | 'credits', number> };
  campaigns: Array<{ id: string; name: string; status: string; leads: number; sent: number; replied: number; bounced: number }>;
  last_replies: Array<{ id: string; at: string; company: string | null; domain: string; contact: string; text: string | null; campaign: string }>;
  hot_leads: Array<{ id: string; status: string; delivered_at: string | null; company: string | null; domain: string; reason: string | null }>;
};

/** A small line over the period, with weekday or date ticks. */
function Spark({ values, days, tone }: { values: number[]; days: string[]; tone: string }) {
  const max = Math.max(1, ...values);
  const w = 240; const h = 54;
  const x = (i: number) => (values.length < 2 ? w / 2 : (i / (values.length - 1)) * (w - 8) + 4);
  const y = (v: number) => h - 6 - (v / max) * (h - 14);
  const label = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
  const ticks: Array<[number, string]> = days.length <= 7 ? days.map((d, i) => [i, label(d)] as [number, string]).filter((_, i) => i % 2 === 0)
    : days.length ? [[0, days[0]!.slice(5)], [days.length - 1, days[days.length - 1]!.slice(5)]] : [];
  return (
    <svg className={`spark ${tone}`} viewBox={`0 0 ${w} ${h + 14}`} role="img" aria-label={values.join(', ')}>
      <line x1="4" x2={w - 4} y1={h - 6} y2={h - 6} className="axis" />
      <polyline points={values.map((v, i) => `${x(i)},${y(v)}`).join(' ')} fill="none" />
      {values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r="2.5" />)}
      {ticks.map(([i, t]) => <text key={i} x={x(i)} y={h + 11} textAnchor={i === 0 ? 'start' : i === values.length - 1 ? 'end' : 'middle'}>{t}</text>)}
    </svg>
  );
}

function Metric({ label, value, values, days, tone }: { label: string; value: string; values: number[]; days: string[]; tone: string }) {
  return <div className="metric"><span className="metric-label">{label}</span><span className="metric-value">{value}</span><Spark values={values} days={days} tone={tone} /></div>;
}

/** Daily budget (as Explee): how many credits Outlevo may spend a day, today's use, and the balance. */
function Budget({ money }: { money: Dash['money'] }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const qc = useQueryClient();
  const [value, setValue] = useState<number>(money.daily_budget ?? 100);
  const [err, setErr] = useState<unknown>(null);
  const save = async (v: number | null) => {
    setErr(null);
    try { await api('/portal/budget', { method: 'PUT', body: { daily_credit_budget: v } }); void qc.invalidateQueries(); } catch (e) { setErr(e); }
  };
  const limited = money.daily_budget !== null;
  return (
    <section className="section card-budget" aria-label="Daily budget">
      <div className="cb-head"><b>Daily budget</b><span className="cb-value">{limited ? `${money.daily_budget} credits` : 'No limit'}</span></div>
      {limited ? <div className="meter" role="img" aria-label={`${money.spent_today} of ${money.daily_budget} used today`}><i style={{ width: `${Math.min(100, (money.spent_today / Math.max(1, money.daily_budget!)) * 100)}%` }} /></div> : null}
      <p className="muted small">{money.spent_today} credits used today{limited ? '' : '. Set a budget to cap what Outlevo spends in a day.'}</p>
      <div className="cb-row">
        <input id={`${uid}-f1`} type="range" min={10} max={1000} step={10} value={value} aria-label="Daily budget in credits"
          onChange={(e) => setValue(Number(e.target.value))} onMouseUp={() => void save(value)} onTouchEnd={() => void save(value)}
          onKeyUp={(e) => { if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) void save(value); }} />
        <span className="cb-pick">{value}</span>
        {limited ? <button type="button" className="link small" onClick={() => void save(null)}>No limit</button> : null}
      </div>
      <div className="cb-foot"><span className="chip">Balance {money.available.toLocaleString('en-US')} credits</span><Link className="btn btn-sm" to="/billing#buy-credits">Top up</Link></div>
      {err ? <ErrorBox error={err} /> : null}
    </section>
  );
}

const ago = (t: string) => { const m = Math.round((Date.now() - Date.parse(t)) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`; };

export function Home() {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const { me } = useAuth();
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState<'replies' | 'hot'>('replies');
  const [filter, setFilter] = useState<'active' | 'all'>('all');
  const d = useApi<Dash>(`/portal/dashboard?days=${days}`);
  const x = d.data;
  // the daily brief's "needs you" and "yesterday", as on the new Home (setup has its own widget here)
  const today = (useApi<{ lines: TodayLine[] }>('/portal/today', { refetchMs: 120_000 }).data?.lines ?? []).filter((l) => l.group);
  const nav = useNavigate();
  const dayList = x?.performance.series.map((r) => r.day) ?? [];
  const col = (k: 'sent' | 'replies' | 'researched' | 'credits') => x?.performance.series.map((r) => r[k]) ?? [];
  return (
    <Page title={me?.org.primary_domain ?? 'Dashboard'}>
      {x?.banner ? (
        <section className={`section dash-banner ${x.banner.tone}`}>
          <span className="dot" aria-hidden="true" />
          <div><b>{x.banner.title}</b><span className="muted">{x.banner.text}</span></div>
          <Link className="btn btn-primary" to={x.banner.cta.to}>{x.banner.cta.label}</Link>
        </section>
      ) : null}
      {today.length ? <TodaySection lines={today} go={(to) => nav(to)} /> : null}
      {me?.org.primary_domain && !me.org.sandbox_of ? <OwnWebsite /> : null}
      <AdsCard />
      {x ? (
        <div className="dash-cards">
          <section className="section card-refer">
            <span className="jicon"><Icon d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM19 8v6M22 11h-6" /></span>
            <div><b>Get ${x.referral.usd_each} per friend</b><span className="muted small">You both get {x.referral.credits_each} credits when they first pay</span></div>
            <Link className="btn btn-primary" to="/refer">Invite a friend</Link>
          </section>
          <Budget money={x.money} />
        </div>
      ) : null}

      <div className="dash-head">
        <h2>Performance</h2>
        <select id={`${uid}-f2`} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Period"><option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option></select>
      </div>
      {x ? (
        <div className="metrics">
          <Metric label="Emails sent" value={x.performance.totals.sent.toLocaleString('en-US')} values={col('sent')} days={dayList} tone="a" />
          <Metric label="Replies" value={x.performance.totals.replies.toLocaleString('en-US')} values={col('replies')} days={dayList} tone="b" />
          <Metric label="Accounts researched" value={x.performance.totals.researched.toLocaleString('en-US')} values={col('researched')} days={dayList} tone="a" />
          <Metric label="Credits spent" value={x.performance.totals.credits.toLocaleString('en-US')} values={col('credits')} days={dayList} tone="c" />
        </div>
      ) : d.error ? <ErrorBox error={d.error} /> : <p className="muted" role="status">Loading…</p>}

      {x ? (
        <>
          <div className="dash-head">
            <h2>Campaigns <span className="count">{x.campaigns.length}</span></h2>
            <div className="dash-tools">
              <Link className="btn btn-sm" to="/kill-switches">Stop campaigns and spending</Link>
              <div className="seg-tabs" role="tablist">{(['active', 'all'] as const).map((f) => <button key={f} type="button" role="tab" aria-selected={filter === f} onClick={() => setFilter(f)}>{f === 'active' ? 'Active' : 'All'}</button>)}</div>
            </div>
          </div>
          <section className="section flush">
            {x.campaigns.length ? (
              <table className="table">
                <thead><tr><th>Campaign</th><th>State</th><th className="num">Leads</th><th className="num">Sent</th><th className="num">Reply rate</th><th className="num">Replies</th><th className="num">Bounced</th></tr></thead>
                <tbody>{x.campaigns.filter((c) => filter === 'all' || c.status === 'active').map((c) => (
                  <tr key={c.id}><td><Link to={`/campaigns/${c.id}`}>{c.name}</Link></td><td><span className={`chip ${c.status === 'active' ? 'done' : ''}`}>{c.status}</span></td>
                    <td className="num">{c.leads}</td><td className="num">{c.sent}</td><td className="num">{c.sent ? `${Math.round((c.replied / c.sent) * 100)}%` : '—'}</td><td className="num">{c.replied}</td><td className="num">{c.bounced}</td></tr>
                ))}</tbody>
              </table>
            ) : <div className="empty-state"><p>No campaigns yet.</p><Link className="btn btn-primary" to="/campaigns/new">New campaign</Link></div>}
          </section>

          <div className="dash-head">
            <div className="seg-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={tab === 'replies'} onClick={() => setTab('replies')}>Last replies</button>
              <button type="button" role="tab" aria-selected={tab === 'hot'} onClick={() => setTab('hot')}>Hot leads</button>
            </div>
            <Link to={tab === 'replies' ? '/replies' : '/handoffs'} className="small">View all →</Link>
          </div>
          <section className="section">
            {tab === 'replies' ? (x.last_replies.length
              ? <ol className="timeline">{x.last_replies.map((r) => <li key={r.id}><span className="tl-dot" aria-hidden="true" /><span className="tl-text"><b>{r.contact || 'Someone'}</b> at {r.company ?? r.domain}: {r.text ? `“${r.text.slice(0, 140)}${r.text.length > 140 ? '…' : ''}”` : 'replied'}</span><time dateTime={r.at}>{ago(r.at)} · {r.campaign}</time></li>)}</ol>
              : <p className="muted center">No replies yet.</p>)
            : (x.hot_leads.length
              ? <ol className="timeline">{x.hot_leads.map((h) => <li key={h.id}><span className="tl-dot" aria-hidden="true" /><span className="tl-text"><Link to="/handoffs"><b>{h.company ?? h.domain}</b></Link>{h.reason ? `: ${h.reason}` : ''}</span><time>{h.delivered_at ? ago(h.delivered_at) : h.status}</time></li>)}</ol>
              : <p className="muted center">No hot leads yet. Accounts ready for sales appear here as Readiness Cards.</p>)}
          </section>
        </>
      ) : null}
      <GetStarted />
    </Page>
  );
}

type SiteSetup = {
  domain: string;
  context: { facts: number; evidence: { id: string; status: string; error: string | null } | null };
  markets: string[];
  icp: { version_id: string | null; hypotheses: number; approved: boolean };
  segments_job: { id: string; status: string } | null;
  assessment: { id: string; segments: number; created_at: string } | null;
  done: boolean;
};

// markets are the person's call: one click on a group, or type ISO codes
const MARKET_GROUPS: Array<[string, string[]]> = [
  ['United States', ['US']], ['Canada', ['CA']], ['United Kingdom', ['GB']], ['English-speaking', ['US', 'CA', 'GB', 'IE', 'AU', 'NZ']],
  ['DACH', ['DE', 'AT', 'CH']], ['EU + UK', ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB']],
];

/** F35: the markets proposed from the website, each with its reason; one press saves the one the person picks. */
function MarketProposals({ busy, onPick }: { busy: boolean; onPick: (codes: string[]) => void }) {
  const [p, setP] = useState<{ markets: Array<{ countries: string[]; why: string }>; note?: string } | null>(null);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => { api<{ markets: Array<{ countries: string[]; why: string }> }>('/portal/site-setup/markets/proposal', { method: 'POST' }).then(setP, setErr); }, []);
  if (err) return null; // the chips below still work
  if (!p) return <span className="muted small" role="status"><span className="spinner" aria-hidden="true" />Proposing your markets from your website…</span>;
  if (!p.markets.length) return null;
  return (
    <ul className="market-proposals" aria-label="Proposed markets">{p.markets.map((m, i) => (
      <li key={m.countries.join()}>
        <button type="button" className={`btn btn-sm${i === 0 ? ' btn-primary' : ''}`} disabled={busy} onClick={() => onPick(m.countries)}>Use {m.countries.join(', ')}</button>
        <span className="muted small">{i === 0 ? 'Recommended. ' : ''}{m.why}</span>
      </li>
    ))}</ul>
  );
}

/**
 * Set the workspace up from its own website: read it (business context), choose markets, draft the ICP segments.
 * Stays until all three are done; each step runs as soon as the one before is.
 */
function OwnWebsite() {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const qc = useQueryClient();
  const [polling, setPolling] = useState(false);
  const s = useApi<SiteSetup>('/portal/site-setup', { refetchMs: polling ? 2500 : undefined });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const d = s.data;
  const reading = Boolean(d?.context.evidence && ['pending', 'extracting'].includes(d.context.evidence.status));
  const drafting = Boolean(d?.segments_job && ['queued', 'running'].includes(d.segments_job.status));
  useEffect(() => { setPolling(reading || drafting); if (!reading && !drafting) void qc.invalidateQueries({ queryKey: ['/portal/onboarding/state'] }); }, [reading, drafting]);
  if (!d || d.done) return null;

  const run = async (fn: () => Promise<unknown>) => { setErr(null); setBusy(true); try { await fn(); await s.refetch(); setPolling(true); } catch (e) { setErr(e); } finally { setBusy(false); } };
  const analyse = () => run(() => api('/v1/evidence', { method: 'POST', body: { kind: 'url', title: `${d.domain} website`, url: `https://${d.domain}/` } }));
  const saveMarkets = () => run(() => api('/portal/site-setup/markets', { method: 'POST', body: { countries: [...picked] } }));
  // a paid proposal comes back as a quote first; the person confirms the credits
  const segments = () => run(async () => {
    const r = await api<{ segments: { confirm_required?: boolean; credits?: number } }>('/portal/site-setup/segments', { method: 'POST' });
    if (r.segments.confirm_required && window.confirm(`Drafting segments again costs ${r.segments.credits} credits. Go ahead?`)) await api('/portal/site-setup/segments', { method: 'POST', body: { confirm: true } });
  });
  const toggleGroup = (codes: string[]) => setPicked((cur) => { const all = codes.every((c) => cur.has(c)); const n = new Set(cur); for (const c of codes) (all ? n.delete(c) : n.add(c)); return n; });

  const contextDone = d.context.facts > 0;
  const marketsDone = d.markets.length > 0;
  const segmentsDone = d.icp.hypotheses > 0 || d.icp.approved;
  const steps: Array<{ key: string; label: string; done: boolean; body: React.ReactNode }> = [
    { key: 'read', label: `Read ${d.domain}`, done: contextDone, body: reading
      ? <span role="status"><span className="spinner" aria-hidden="true" />Reading the site and drafting facts with their sources…</span>
      : d.context.evidence?.status === 'failed' ? <><span className="error small">The site could not be read: {d.context.evidence.error ?? 'no detail'}.</span> <button type="button" className="btn btn-sm" disabled={busy} onClick={analyse}>Try again</button></>
      : contextDone ? <span className="muted small">{d.context.facts} facts drafted. <Link to="/context">Review them</Link></span>
      : <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={analyse}>Analyse my website</button> },
    { key: 'markets', label: 'Choose the countries you sell into', done: marketsDone, body: marketsDone
      ? <span className="muted small">{d.markets.join(', ')} · <Link to="/icp">change</Link></span>
      : <div className="market-pick">
          <MarketProposals busy={busy} onPick={(codes) => run(() => api('/portal/site-setup/markets', { method: 'POST', body: { countries: codes } }))} />
          <span className="muted small">Or choose them yourself:</span>
          <div className="chips">{MARKET_GROUPS.map(([label, codes]) => (
            <button key={label} type="button" className={`chip-btn${codes.every((c) => picked.has(c)) ? ' on' : ''}`} aria-pressed={codes.every((c) => picked.has(c))} onClick={() => toggleGroup(codes)}>{label}</button>
          ))}</div>
          <input id={`${uid}-f3`} aria-label="Other countries (ISO codes)" placeholder="Other countries, e.g. FR, NL" onBlur={(e) => { const codes = e.currentTarget.value.split(/[\s,]+/).filter((c) => /^[a-z]{2}$/i.test(c)).map((c) => c.toUpperCase()); if (codes.length) setPicked((cur) => new Set([...cur, ...codes])); }} />
          <button type="button" className="btn btn-primary btn-sm" disabled={busy || !picked.size} onClick={saveMarkets}>Save {picked.size ? `${picked.size} ${picked.size === 1 ? 'country' : 'countries'}` : 'markets'}</button>
        </div> },
    { key: 'segments', label: 'Draft your ICP segments', done: segmentsDone, body: drafting
      ? <span role="status"><span className="spinner" aria-hidden="true" />Drafting segments from your business context…</span>
      : segmentsDone ? <span className="muted small">{d.icp.hypotheses} segments drafted. <Link to="/icp">Review and submit</Link></span>
      : !marketsDone ? <span className="muted small">Runs as soon as your markets are set.</span>
      : <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={segments}>{d.assessment ? `Use my assessment (${d.assessment.segments} segments)` : 'Draft segments'}</button> },
  ];
  const done = steps.filter((x) => x.done).length;
  return (
    <section className="section own-site" aria-label="Set up from your website">
      <div className="own-head">
        <span className="jicon"><Icon d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></span>
        <div><b>Set up from your website: <code>{d.domain}</code></b><span className="muted small"> · {done} of 3 done</span></div>
      </div>
      <ol className="own-steps">{steps.map((x, i) => (
        <li key={x.key} className={x.done ? 'done' : ''}>
          <span className="own-n" aria-hidden="true">{x.done ? '✓' : i + 1}</span>
          <div><b>{x.label}</b><div>{x.body}</div></div>
        </li>
      ))}</ol>
      {err ? <ErrorBox error={err} /> : null}
    </section>
  );
}

/** Research any company's website: it becomes an account (or opens the existing one) with the research panel. */
export function ResearchWebsite() {
  const nav = useNavigate();
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const go = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr(null); setBusy(true);
    const domain = String(new FormData(e.currentTarget).get('domain') ?? '');
    try {
      const c = await api<{ id: string }>('/v1/companies', { method: 'POST', body: { domain } });
      nav(`/accounts/${c.id}`);
    } catch (x) {
      const id = (x as { details?: { company_id?: string } }).details?.company_id;
      if (id) nav(`/accounts/${id}`); else setErr(x);
    } finally { setBusy(false); }
  };
  return (
    <form className="research-box" onSubmit={go} aria-label="Research a website">
      <label htmlFor="rw" className="sr-only">Website to research</label>
      <span className="jicon"><Icon d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3" /></span>
      <input id="rw" name="domain" required placeholder="Research a company's website, e.g. acme.com" autoComplete="off" />
      <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Opening…' : 'Research'}</button>
      {err ? <ErrorBox error={err} /> : null}
    </form>
  );
}
